import React, { CSSProperties, ElementType, useState, useRef, useEffect } from 'react';

import classNames from 'classnames';

import { SvgImage } from '@/components/Elements/SvgImage/SvgImage';

import { IImage } from '@/types/types';

import DefaultImage from '@/images/noImage.svg';

import './AdaptiveImage.scss';

interface IAdaptiveImageSource {
  sizePrefix: string;
  mime: string;
  width: number;
  fileName: string;
}

interface Props {
  alt: string;
  as?: ElementType;
  image: IImage;
  className?: string;
  style?: CSSProperties;
  loading?: 'eager' | 'lazy';
  backgroundColor?: string;
  objectFit?: CSSProperties['objectFit'];
  objectPosition?: CSSProperties['objectPosition'];
  maxWidth?: number; // we needed that prop because there are old images, in which names of sizes (md and lg) match the other sizes (plugin was changed after uploading images)
}

const sortMimeGropedSources = (mimeGroupedSources: IAdaptiveImageSource[][]) => {
  const webpMime: string = 'image/webp';

  mimeGroupedSources.sort((a, b) => {
    if (a[0].mime === webpMime) return -1;

    if (b[0].mime === webpMime) return 1;

    return 0;
  });

  return mimeGroupedSources;
};

const groupSourcesByMimeType = (sources: IAdaptiveImageSource[]) => {
  const mimeGroupedSourcesRecords = sources.reduce((acc: Record<string, IAdaptiveImageSource[]>, source) => {
    (acc[source.mime] = acc[source.mime] || []).push(source);

    return acc;
  }, {});

  const mimeGroupedSources: IAdaptiveImageSource[][] = Object.values(mimeGroupedSourcesRecords);
  const sortedMimeGroupedSources: IAdaptiveImageSource[][] = sortMimeGropedSources(mimeGroupedSources);

  return sortedMimeGroupedSources;
};

const AdaptiveImage = ({
  alt,
  as = `div`,
  image,
  className,
  style,
  loading = `lazy`,
  backgroundColor,
  objectFit,
  objectPosition,
  maxWidth = 1280,
}: Props) => {
  // If no image is provided, render a default image
  if (!image) {
    return <SvgImage src={DefaultImage} alt="No image" className={classNames('AdaptiveImage_svg', className)} />;
  }

  const fileStorageUrl = process.env.IMAGE_STORAGE_PATH;
  const defaultImageFileName = image.hash + image.ext;
  const defaultImagePath = fileStorageUrl + defaultImageFileName;

  // If the image is an SVG, render it as an SvgImage
  // There is a bug in strapi:
  // when you replace an image through Replace media button, it doesn't update ext value in database,
  // which results in wrong ext values for images.
  // That's why we use this hack to render SvgImage if there are no formats values (tn, lg and others) present
  // (considering that if image doesn't have them, it is svg)
  if (image.ext === '.svg' || Object.values(image.formats).every((value) => !value)) {
    return (
      <SvgImage
        src={defaultImagePath}
        alt={alt}
        width={image.width}
        height="auto"
        className={classNames('AdaptiveImage_svg', className)}
      />
    );
  }

  // If the image formats (thumbnail and others) are present, provide an adaptive image logic
  // If we got there, so every format key is present
  // (we checked for  Object.values(image.formats).every((value) => !value)),
  // so we use ! further to avoid TS errors
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // we need imgRef.current?.complete because when images are cached, onLoad is not triggered
    if (imgRef.current?.complete) {
      setIsLoaded(true);
    }
  }, []);

  const thumbnailImageFileName = image.formats.tn!.hash + image.formats.tn!.ext;
  const thumbnailImagePath = fileStorageUrl + thumbnailImageFileName;

  const fallbackImageFileName = image.formats.md_webp!.hash + image.formats.md_webp!.ext;
  const fallbackImagePath = fileStorageUrl + fallbackImageFileName;

  const imgStyle: CSSProperties = {
    backgroundColor,
    objectFit,
    objectPosition,
  };

  const allSources: IAdaptiveImageSource[] = Object.entries(image.formats)
    .slice(1) // remove thumbnail format as it will be rendered as a separate img tag
    .reduce((acc, [sizePrefix, format]) => {
      if (format!.width <= maxWidth) {
        acc.push({
          sizePrefix,
          mime: format!.mime,
          width: format!.width,
          fileName: format!.hash + format!.ext,
        });
      }

      return acc;
    }, [] as IAdaptiveImageSource[]);
  const mimeGroupedSources: IAdaptiveImageSource[][] = groupSourcesByMimeType(allSources);

  return React.createElement(
    as,
    {
      style: { ...style, backgroundColor },
      className: classNames('AdaptiveImage', className),
    },
    <>
      {}
      <img
        decoding="async"
        loading="eager" // check it if it would affect page performance. Maybe it should be the same as the main image (loading)
        className={classNames('AdaptiveImage_thumbnail', { loaded: isLoaded }, {})}
        style={imgStyle}
        width={image.width}
        height={image.height}
        src={thumbnailImagePath}
        alt={alt}
      />

      <picture>
        {mimeGroupedSources.map((sourceGroup) => (
          <React.Fragment key={sourceGroup[0].mime}>
            {sourceGroup.map((source, index) => {
              const isLastSourceInGroup = index === sourceGroup.length - 1;

              return (
                <source
                  key={source.sizePrefix}
                  srcSet={`${fileStorageUrl}${source.fileName} ${source.width}w`}
                  media={isLastSourceInGroup ? '' : `(max-width: ${source.width}px)`}
                  type={source.mime}
                />
              );
            })}
          </React.Fragment>
        ))}
        <img
          ref={imgRef}
          decoding="async"
          loading={loading}
          className={classNames('AdaptiveImage_main', { loaded: isLoaded })}
          style={imgStyle}
          width={image.width}
          height={image.height}
          src={fallbackImagePath || defaultImagePath}
          alt={alt}
          onLoad={() => setIsLoaded(true)}
        />
      </picture>
    </>,
  );
};

export default AdaptiveImage;
